/**
 * Data the homepage blocks share (src/components/home/*).
 *
 * The blocks used to call useCompounds(), the full list query: about 770 KB on the wire, nearly all of it
 * the markdown descriptions, which the homepage never shows. They need names, slugs, categories, view counts
 * and a few facts, so they share this light query instead (about 70 KB before compression). Nothing else on
 * the homepage asks for the full list, so a homepage visit no longer downloads it.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useListings, type DbListing } from "@/hooks/useDatabase";
import { isBuyable, lowestPriceListing, priceRange } from "@/lib/listingPicks";

export type HomeCompound = {
  id: string;
  name: string;
  slug: string;
  category: string;
  featured: boolean;
  view_count: number | null;
  research_stage: string | null;
  pubmed_count: number | null;
  structure_image_url: string | null;
};

const HOME_COMPOUND_COLUMNS = "id,name,slug,category,featured,view_count,research_stage,pubmed_count,structure_image_url";

/** Every compound, light columns only, ordered by name (the order GoalPage matches names in). */
export function useHomeCompounds() {
  return useQuery({
    queryKey: ["home-compounds"],
    queryFn: async () => {
      const { data, error } = await supabase.from("compounds").select(HOME_COMPOUND_COLUMNS).order("name");
      if (error) throw error;
      return (data ?? []) as unknown as HomeCompound[];
    },
  });
}

/**
 * The compound's structure image, or null when the row has none. On 22 October 2026 most structure_image_url
 * values pointed at the wrong PubChem compound, so the homepage only showed a short list of checked ones. Database
 * batches 1, 2 and f and the 31 October audit (scripts/pubchem-cid-audit.mjs, applied 3 November) corrected the
 * table, so that list is gone. Run the audit again after adding compounds.
 */
export function structureUrl(c: Pick<HomeCompound, "structure_image_url">): string | null {
  return (c.structure_image_url ?? "").trim() || null;
}

/**
 * The goals that are not tiles on the homepage. From 640px they are chips under the tiles (HomeGoals); on a phone
 * they sit in "Browse more" (HomeLatest). /goal/cognition redirects to /goal/focus, so it gets none.
 */
export const MORE_GOALS: { slug: string; label: string }[] = [
  { slug: "energy", label: "Energy" },
  { slug: "stress", label: "Stress and mood" },
  { slug: "dopamine", label: "Dopamine" },
  { slug: "social", label: "Social" },
  { slug: "neuroplasticity", label: "Neuroplasticity" },
];

/** The research stage's first clause ("Phase III", "FDA Approved"), so a long note does not fill the line. */
export function shortStage(stage: string | null | undefined): string | null {
  const s = (stage ?? "").trim();
  if (!s) return null;
  const cut = s.search(/[;(]|\.\s/);
  const clause = (cut > 0 ? s.slice(0, cut) : s).trim().replace(/[.,]$/, "");
  return clause.length <= 32 ? clause : null;
}

/**
 * The clinical trial phase a research stage names ("Phase III", "Phase 2", "Phase 2/3"), read from its first clause
 * (shortStage), or null. Any phase means the compound has reached human trials.
 */
export function trialPhase(stage: string | null | undefined): string | null {
  const clause = shortStage(stage);
  if (!clause) return null;
  const m = clause.match(/\bphase\s*((?:[0-9]+|[ivx]+)[ab]?(?:\s*\/\s*(?:[0-9]+|[ivx]+)[ab]?)?)\b/i);
  return m ? `Phase ${m[1].replace(/\s+/g, "")}` : null;
}

/**
 * The most viewed compound whose research stage names a trial phase: the hero's "Human trials" card. On a tie the
 * first in the list wins (useHomeCompounds orders by name), so the pick does not change between loads.
 */
export function mostViewedInTrials(compounds: HomeCompound[]): { compound: HomeCompound; phase: string } | null {
  let best: { compound: HomeCompound; phase: string } | null = null;
  for (const compound of compounds) {
    const phase = trialPhase(compound.research_stage);
    if (!phase) continue;
    if (!best || (compound.view_count ?? 0) > (best.compound.view_count ?? 0)) best = { compound, phase };
  }
  return best;
}

export type PriceRow = {
  compound: HomeCompound;
  /** The lowest price per mg among buyable listings with an mg count (lowestPriceListing). */
  lowest: DbListing;
  /** Each vendor's lowest price per mg in the same pool, ascending. Empty when no listing has an mg count. */
  perVendor: number[];
  /** Vendors with a priced listing, as the compound page counts them (priceRange). */
  vendorCount: number;
};

/**
 * The most viewed compounds that have a price, with the lowest price per mg on file and every vendor's lowest
 * price per mg (for the dot plot). Ordered by page views across the whole table.
 */
export function useHomePriceRows(count: number) {
  const { data: compounds = [], isPending: compoundsPending } = useHomeCompounds();
  const { data: listings = [], isPending: listingsPending } = useListings();

  const rows = useMemo(() => {
    const byCompound = new Map<string, DbListing[]>();
    for (const l of listings) {
      const list = byCompound.get(l.compound_id);
      if (list) list.push(l);
      else byCompound.set(l.compound_id, [l]);
    }
    const out: PriceRow[] = [];
    const ranked = compounds.filter((c) => byCompound.has(c.id)).sort((a, b) => (b.view_count ?? 0) - (a.view_count ?? 0));
    for (const compound of ranked) {
      if (out.length >= count) break;
      const own = byCompound.get(compound.id) ?? [];
      const lowest = lowestPriceListing(own);
      if (!lowest || lowest.price <= 0) continue;
      // The same pool lowestPriceListing compares: buyable listings with an mg count, or every priced one with an
      // mg count when none is buyable.
      const withMg = own.filter((l) => l.price > 0 && l.total_mg > 0);
      const buyable = withMg.filter(isBuyable);
      const pool = buyable.length > 0 ? buyable : withMg;
      const best = new Map<string, number>();
      for (const l of pool) {
        const v = l.price / l.total_mg;
        const prev = best.get(l.vendor_id);
        if (prev === undefined || v < prev) best.set(l.vendor_id, v);
      }
      out.push({
        compound,
        lowest,
        perVendor: [...best.values()].sort((a, b) => a - b),
        vendorCount: priceRange(own).vendorCount,
      });
    }
    return out;
  }, [compounds, listings, count]);

  return { rows, loading: rows.length === 0 && (compoundsPending || listingsPending) };
}

/** How many rows the prices section shows. The hero's "Lowest listed" card picks from the same rows. */
export const HOME_PRICE_ROWS = 6;

/**
 * Of the rows the prices section shows, the one whose lowest listing costs least per mg: the hero's "Lowest listed"
 * card. Rows priced only per pack (no mg count) are skipped, since the card shows a price per mg.
 */
export function lowestPerMgRow(rows: PriceRow[]): PriceRow | null {
  let best: PriceRow | null = null;
  let bestValue = Infinity;
  for (const row of rows) {
    const { price, total_mg } = row.lowest;
    if (!(price > 0) || !(total_mg > 0)) continue;
    const value = price / total_mg;
    if (value < bestValue) {
      best = row;
      bestValue = value;
    }
  }
  return best;
}

/** The two facts on the hero's floating cards (shown from 1024px), from data the homepage already loads. */
export function useHeroFacts() {
  const { rows, loading: pricesLoading } = useHomePriceRows(HOME_PRICE_ROWS);
  const { data: compounds = [], isPending } = useHomeCompounds();
  const lowest = useMemo(() => lowestPerMgRow(rows), [rows]);
  const trials = useMemo(() => mostViewedInTrials(compounds), [compounds]);
  return { lowest, pricesLoading, trials, compoundCount: compounds.length, compoundsLoading: isPending && compounds.length === 0 };
}

/** Listings grouped by compound id. */
export function listingsByCompound(listings: DbListing[]): Map<string, DbListing[]> {
  const out = new Map<string, DbListing[]>();
  for (const l of listings) {
    const list = out.get(l.compound_id);
    if (list) list.push(l);
    else out.set(l.compound_id, [l]);
  }
  return out;
}

/**
 * A goal tile's numbers, agreeing with the goal page the tile opens. GoalPage shows the goal's names (lib/goalData.ts)
 * that match a compound's name exactly, taking the first match in name order, and prices each card with priceRange
 * (buyable listings first). The count is those matches; "from" is the lowest of those card prices, or null when none
 * has a price. A few names exist twice in the table (an old alias row beside the real one); the first match wins, as
 * in GoalPage.
 */
export function goalTileStats(
  names: string[],
  compounds: Pick<HomeCompound, "id" | "name">[],
  byCompound: Map<string, DbListing[]>,
): { count: number; from: number | null } {
  const firstByName = new Map<string, string>();
  for (const c of compounds) {
    const key = c.name.toLowerCase();
    if (!firstByName.has(key)) firstByName.set(key, c.id);
  }
  let count = 0;
  let from: number | null = null;
  for (const name of names) {
    const id = firstByName.get(name.toLowerCase());
    if (!id) continue;
    count++;
    const lowest = priceRange(byCompound.get(id) ?? []).lowest;
    if (lowest > 0 && (from === null || lowest < from)) from = lowest;
  }
  return { count, from };
}
